import { Archive, Ban, CircleCheck, Pencil, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'

import type {
  LedgerGlAccountClass,
  LedgerGlAccountNode,
  LedgerGlAccountResult,
} from '@/api/ledger-types'
import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

import { GL_ACCOUNT_CLASS_LABEL } from './constants'
import { LedgerDualControlNotice, LedgerDualControlOutcome } from './dual-control'
import { useCreateGlAccount, useUpdateGlAccount } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'

/**
 * Maintaining the chart of accounts — the manage half the gap list used to name.
 *
 * Every operation here is countersigned under `ledger.account.manage`, and every dialog reads the
 * envelope rather than assuming: a queued create shows no new node, and a queued block leaves the
 * account taking postings, and saying otherwise would be the exact lie the dual-control components
 * exist to prevent.
 *
 * Structure is fixed at creation — code, class, parent, postability — because balances post against
 * that identity. The edit dialog therefore carries only what may change: name, description, return
 * line, status. A differently-shaped account is a new account.
 */

const CLASSES: LedgerGlAccountClass[] = ['Asset', 'Liability', 'Equity', 'Income', 'Expense']

const CLASS_DIGIT: Record<LedgerGlAccountClass, string> = {
  Asset: '1',
  Liability: '2',
  Equity: '3',
  Income: '4',
  Expense: '5',
}

const CODE_SHAPE = /^[1-5]-\d{2}-\d{4}$/

const NO_PARENT = '__root__'

/** Headings only, flattened with their depth — the only accounts allowed to take children. */
const flattenHeadings = (
  nodes: LedgerGlAccountNode[],
  depth = 0,
): { code: string; name: string; depth: number }[] =>
  nodes.flatMap((node) =>
    node.isPostable || node.status === 'Archived'
      ? []
      : [
          { code: node.code, name: node.name, depth },
          ...flattenHeadings(node.children, depth + 1),
        ],
  )

export function CreateGlAccountAction({ roots }: { roots: LedgerGlAccountNode[] }) {
  const permissions = useLedgerPermissions()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [accountClass, setAccountClass] = useState<LedgerGlAccountClass>('Asset')
  const [parentCode, setParentCode] = useState<string>(NO_PARENT)
  const [isPostable, setIsPostable] = useState(true)
  const [returnLine, setReturnLine] = useState('')
  const [description, setDescription] = useState('')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<LedgerGlAccountResult | null>(null)

  const create = useCreateGlAccount()

  useEffect(() => {
    if (!open) {
      setCode('')
      setName('')
      setAccountClass('Asset')
      setParentCode(NO_PARENT)
      setIsPostable(true)
      setReturnLine('')
      setDescription('')
      setReason('')
      setResult(null)
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset() is stable; `create` would re-run this on every mutation state change
  }, [open])

  if (!permissions.canManageAccounts) {
    return null
  }

  const headings = flattenHeadings(roots)
  const trimmedCode = code.trim()
  const codeShapeOk = CODE_SHAPE.test(trimmedCode)
  const codeClassOk = !codeShapeOk || trimmedCode.startsWith(CLASS_DIGIT[accountClass])
  const ready =
    codeShapeOk && codeClassOk && name.trim() !== '' && reason.trim().length >= 12

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Plus />
        Add an account
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add an account to the chart</DialogTitle>
            <DialogDescription>
              Code, class, parent and postability are fixed forever once created — they are what
              postings will be recorded against.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-3">
            {result === null ? (
              <>
                <LedgerDualControlNotice
                  action="Changing the chart of accounts"
                  approvePermission={LEDGER_PERMISSIONS.accountManageApprove}
                  canCountersign={permissions.canCountersignAccountManage}
                />

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-account-class">Class</Label>
                    <Select
                      value={accountClass}
                      onValueChange={(next) => setAccountClass(next as LedgerGlAccountClass)}
                    >
                      <SelectTrigger id="new-account-class">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CLASSES.map((candidate) => (
                          <SelectItem key={candidate} value={candidate}>
                            {CLASS_DIGIT[candidate]} — {GL_ACCOUNT_CLASS_LABEL[candidate]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="new-account-code">Code</Label>
                    <Input
                      id="new-account-code"
                      value={code}
                      autoComplete="off"
                      placeholder={`${CLASS_DIGIT[accountClass]}-05-1050`}
                      onChange={(event) => setCode(event.target.value)}
                      className="font-mono"
                      aria-describedby="new-account-code-hint"
                    />
                    <p id="new-account-code-hint" className="text-2xs text-muted">
                      {trimmedCode !== '' && !codeShapeOk
                        ? 'C-GG-AAAA: one class digit, two-digit group, four-digit account.'
                        : trimmedCode !== '' && !codeClassOk
                          ? `A ${GL_ACCOUNT_CLASS_LABEL[accountClass].toLowerCase()} account's code starts with ${CLASS_DIGIT[accountClass]}.`
                          : 'The leading digit repeats the class.'}
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="new-account-name">Name</Label>
                  <Input
                    id="new-account-name"
                    value={name}
                    autoComplete="off"
                    placeholder="What the account holds"
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="new-account-parent">Sits under</Label>
                  <Select value={parentCode} onValueChange={setParentCode}>
                    <SelectTrigger id="new-account-parent">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_PARENT}>Nothing — a root heading</SelectItem>
                      {headings.map((heading) => (
                        <SelectItem key={heading.code} value={heading.code}>
                          {`${' '.repeat(heading.depth)}${heading.code} ${heading.name}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-2xs text-muted">
                    Only headings are offered. A postable account can never take children — a balance
                    must never be split between a parent and its own children.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-sm text-body">
                  <Checkbox
                    checked={isPostable}
                    onCheckedChange={(next) => setIsPostable(next === true)}
                  />
                  Takes postings (a leaf, not a heading)
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-account-return-line">BoG return line</Label>
                    <Input
                      id="new-account-return-line"
                      value={returnLine}
                      autoComplete="off"
                      placeholder="Optional"
                      onChange={(event) => setReturnLine(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-account-description">Description</Label>
                    <Input
                      id="new-account-description"
                      value={description}
                      autoComplete="off"
                      placeholder="Optional"
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </div>
                </div>

                <ReasonField
                  id="new-account-reason"
                  value={reason}
                  onChange={setReason}
                  placeholder="Why the chart needs this account."
                />

                {create.isError ? <ProblemAlert error={create.error} /> : null}
              </>
            ) : (
              <LedgerDualControlOutcome
                result={result}
                awaitingDescription={
                  <>
                    The request to add <span className="font-mono text-xs">{trimmedCode}</span> is
                    stored and waiting for a countersignature. <strong>The chart is unchanged</strong>{' '}
                    and nothing can post to it yet.
                  </>
                }
                appliedDescription={
                  <>
                    <span className="font-mono text-xs">{result.account?.code ?? trimmedCode}</span>{' '}
                    is in the chart{result.account?.isPostable ? ' and can be posted to' : ' as a heading'}.
                  </>
                }
              />
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={create.isPending}>
              {result === null ? 'Cancel' : 'Close'}
            </Button>
            {result === null ? (
              <Button
                variant="primary"
                disabled={!ready || create.isPending}
                onClick={() => {
                  create.mutate(
                    {
                      code: trimmedCode,
                      name: name.trim(),
                      class: accountClass,
                      parentCode: parentCode === NO_PARENT ? null : parentCode,
                      isPostable,
                      boGReturnLineCode: returnLine.trim() === '' ? null : returnLine.trim(),
                      description: description.trim() === '' ? null : description.trim(),
                      reason: reason.trim(),
                    },
                    { onSuccess: setResult },
                  )
                }}
              >
                {create.isPending ? 'Working…' : 'Add the account'}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * The maintenance strip on an account's detail panel: amend what it says, or move its status.
 *
 * Archived accounts get no strip at all — archiving is terminal, and offering an edit that the
 * server will refuse with `ledger.account_archived` would be a button-shaped lie.
 */
export function GlAccountMaintenanceActions({ account }: { account: LedgerGlAccountNode }) {
  const permissions = useLedgerPermissions()
  const [dialog, setDialog] = useState<'amend' | 'block' | 'unblock' | 'archive' | null>(null)

  if (!permissions.canManageAccounts || account.status === 'Archived') {
    return null
  }

  return (
    <div className="border-t border-border-subtle pt-3">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Maintenance</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" onClick={() => setDialog('amend')}>
          <Pencil />
          Amend
        </Button>
        {account.status === 'Active' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDialog('block')}
            disabled={account.isSystemAccount}
            title={
              account.isSystemAccount
                ? 'A module posts to this account by name; blocking it would stop the posting engine.'
                : undefined
            }
          >
            <Ban />
            Block
          </Button>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setDialog('unblock')}>
            <CircleCheck />
            Unblock
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setDialog('archive')}
          disabled={account.isSystemAccount}
          title={
            account.isSystemAccount
              ? 'A module posts to this account by name; it cannot be archived while anything maps to it.'
              : undefined
          }
        >
          <Archive />
          Archive
        </Button>
      </div>
      <p className="mt-2 text-2xs text-muted">
        Countersigned under <span className="font-mono">{LEDGER_PERMISSIONS.accountManage}</span>.
        Blocking stops every branch posting here at once; archiving is permanent and needs the
        account empty.
      </p>

      <MaintainGlAccountDialog account={account} mode={dialog} onClose={() => setDialog(null)} />
    </div>
  )
}

const MODE_COPY = {
  amend: {
    title: (code: string) => `Amend ${code}`,
    description:
      'Name, description and return-line mapping. The code, class, parent and postability are the account’s structure and cannot change.',
    confirm: 'Save the amendment',
    reasonPlaceholder: 'What is changing, and why.',
  },
  block: {
    title: (code: string) => `Block ${code}`,
    description:
      'A blocked account refuses every new posting, at every branch, until it is unblocked. Balances already on it are untouched.',
    confirm: 'Block the account',
    reasonPlaceholder: 'Why postings to this account must stop.',
  },
  unblock: {
    title: (code: string) => `Unblock ${code}`,
    description: 'Postings resume at every branch the moment this takes effect.',
    confirm: 'Unblock the account',
    reasonPlaceholder: 'Why the account is safe to post to again.',
  },
  archive: {
    title: (code: string) => `Archive ${code}`,
    description:
      'Permanent. The account is kept for historical reporting, never posted to again, and its code is never reused. Refused while it holds a balance or has unretired children.',
    confirm: 'Archive the account',
    reasonPlaceholder: 'Why the account is being retired.',
  },
} as const

function MaintainGlAccountDialog({
  account,
  mode,
  onClose,
}: {
  account: LedgerGlAccountNode
  mode: 'amend' | 'block' | 'unblock' | 'archive' | null
  onClose: () => void
}) {
  const permissions = useLedgerPermissions()
  const [name, setName] = useState(account.name)
  const [description, setDescription] = useState(account.description ?? '')
  const [returnLine, setReturnLine] = useState(account.boGReturnLineCode ?? '')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<LedgerGlAccountResult | null>(null)

  const update = useUpdateGlAccount()
  const open = mode !== null

  // Re-primed each time it opens: the panel can move to a different account between openings.
  useEffect(() => {
    if (open) {
      setName(account.name)
      setDescription(account.description ?? '')
      setReturnLine(account.boGReturnLineCode ?? '')
      setReason('')
      setResult(null)
      update.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset() is stable; `update` would re-run this on every mutation state change
  }, [open, account])

  if (mode === null) {
    return null
  }

  const copy = MODE_COPY[mode]
  const targetStatus =
    mode === 'block' ? 'Blocked' : mode === 'unblock' ? 'Active' : mode === 'archive' ? 'Archived' : account.status

  const ready = name.trim() !== '' && reason.trim().length >= 12

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.title(account.code)}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {result === null ? (
            <>
              <LedgerDualControlNotice
                action="Changing the chart of accounts"
                approvePermission={LEDGER_PERMISSIONS.accountManageApprove}
                canCountersign={permissions.canCountersignAccountManage}
              />

              {mode === 'amend' ? (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="amend-account-name">Name</Label>
                    <Input
                      id="amend-account-name"
                      value={name}
                      autoComplete="off"
                      onChange={(event) => setName(event.target.value)}
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="amend-account-return-line">BoG return line</Label>
                      <Input
                        id="amend-account-return-line"
                        value={returnLine}
                        autoComplete="off"
                        placeholder="Untagged"
                        onChange={(event) => setReturnLine(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="amend-account-description">Description</Label>
                      <Input
                        id="amend-account-description"
                        value={description}
                        autoComplete="off"
                        placeholder="Optional"
                        onChange={(event) => setDescription(event.target.value)}
                      />
                    </div>
                  </div>
                </>
              ) : null}

              <ReasonField
                id="maintain-account-reason"
                value={reason}
                onChange={setReason}
                placeholder={copy.reasonPlaceholder}
              />

              {update.isError ? <ProblemAlert error={update.error} /> : null}
            </>
          ) : (
            <LedgerDualControlOutcome
              result={result}
              awaitingDescription={
                <>
                  The request on <span className="font-mono text-xs">{account.code}</span> is stored
                  and waiting for a countersignature. <strong>The account is unchanged</strong>
                  {mode === 'block' ? ' and still accepts postings' : null}.
                </>
              }
              appliedDescription={
                <>
                  <span className="font-mono text-xs">{account.code}</span>{' '}
                  {mode === 'amend'
                    ? 'has been amended.'
                    : mode === 'block'
                      ? 'is blocked — nothing posts to it until it is unblocked.'
                      : mode === 'unblock'
                        ? 'is active again and accepts postings.'
                        : 'is archived. It is history now, and its code is never reused.'}
                </>
              }
            />
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            {result === null ? 'Cancel' : 'Close'}
          </Button>
          {result === null ? (
            <Button
              variant={mode === 'archive' || mode === 'block' ? 'danger' : 'primary'}
              disabled={!ready || update.isPending}
              onClick={() => {
                update.mutate(
                  {
                    code: account.code,
                    request: {
                      name: name.trim(),
                      description: description.trim() === '' ? null : description.trim(),
                      boGReturnLineCode: returnLine.trim() === '' ? null : returnLine.trim(),
                      status: targetStatus,
                      reason: reason.trim(),
                    },
                  },
                  { onSuccess: setResult },
                )
              }}
            >
              {update.isPending ? 'Working…' : copy.confirm}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ReasonField({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string
  value: string
  onChange: (next: string) => void
  placeholder: string
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Reason</Label>
      <textarea
        id={id}
        rows={2}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        aria-describedby={`${id}-hint`}
        className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
      />
      <p id={`${id}-hint`} className="text-xs text-muted">
        Mandatory. It is what the checker reads before signing and what an examiner reads afterwards.
      </p>
    </div>
  )
}
